import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Floating WhatsApp button — MOBILE ONLY.
 * Mounted once globally in App.js. Automatically appears on EVERY page.
 *
 * To customize the prefilled message for a new page:
 *   add an entry to PAGE_CONTEXT below (key = route path, value = Hebrew topic).
 * Anything not in the map falls back to the default greeting.
 *
 * Phone number lives in WHATSAPP_PHONE_E164 (Israel +972 format, leading 0 dropped).
 *
 * On purpose:
 *  - Visual position: bottom-LEFT in RTL (Hebrew sites convention) to avoid
 *    colliding with the right-side JoinedBubble on /join-club and similar.
 *  - Hidden on `/admin*` (internal tool, no public CTA needed).
 *  - Uses onClick → window.open() instead of <a href> to side-step the global
 *    `a[href*="emergent"]` CSS rule in public/index.html that hides any anchor
 *    whose href contains the *.emergentagent.com origin embedded in a share URL.
 */
const WHATSAPP_PHONE_E164 = '972554425912'; // 055-4425912

const PAGE_CONTEXT = {
  '/': 'שלום, יש לי שאלה דרך אתר המרכז הישראלי לעצמאות דיגיטלית',
  '/join-club': 'שלום, אני מתעניין במועדון הפיפיסיסטים (Join Club)',
  '/black-box': 'שלום, אני מתעניין בפרוטוקול הקופסה השחורה',
  '/ppc-rebel': 'שלום, אני מתעניין בתכנית מרד הפיפיסיסטים',
  '/affiliate-business': 'שלום, אני מתעניין בתכנית Affiliate Business',
  '/training-program': 'שלום, אני מתעניין בתכנית ההכשרה',
  '/training-questionnaire': 'שלום, יש לי שאלה לגבי שאלון ההצטרפות לתכנית ההכשרה',
  '/ai-agency': 'שלום, אני מתעניין בסוכנות ה-AI',
  '/ai-calculator': 'שלום, יש לי שאלה לגבי מחשבון ה-AI',
  '/partner-track': 'שלום, אני מתעניין במסלול השותפים',
  '/opm': 'שלום, אני מתעניין בתכנית OPM',
  '/cpa-master': 'שלום, אני מתעניין בתכנית CPA Master',
  '/executives': 'שלום, אני מתעניין במסלול ההסבה לבכירים',
  '/merchants': 'שלום, אני מתעניין בגיוס עסקים שותפים',
  '/affiliate-club': 'שלום, אני מתעניין בהצטרפות למועדון השותפים',
  '/profit-calculator': 'שלום, יש לי שאלה לגבי מחשבון הרווחים',
  '/events': 'שלום, יש לי שאלה לגבי האירועים הקרובים',
  '/hackathon': 'שלום, יש לי שאלה לגבי ההאקתון (30.6.26)',
  '/live-workshop-june-2026': 'שלום, יש לי שאלה לגבי הוורקשופ ביוני 2026',
  '/club-questionnaire': 'שלום, יש לי שאלה לגבי שאלון ההצטרפות למועדון',
  '/blog': 'שלום, יש לי שאלה לגבי כתבה שקראתי באתר',
  '/blog/news': 'שלום, יש לי שאלה לגבי כתבה שקראתי באתר',
  '/terms': 'שלום, יש לי שאלה לגבי תנאי השימוש באתר',
  '/privacy': 'שלום, יש לי שאלה לגבי מדיניות הפרטיות באתר',
  '/accessibility': 'שלום, יש לי שאלה לגבי נגישות האתר',
};

const DEFAULT_MESSAGE = 'שלום, יש לי שאלה דרך אתר המרכז הישראלי לעצמאות דיגיטלית';

/** Resolve the best context message for a given pathname. */
const resolveMessage = (pathname) => {
  if (!pathname) return DEFAULT_MESSAGE;
  // Exact match first
  if (PAGE_CONTEXT[pathname]) return PAGE_CONTEXT[pathname];
  // Dynamic news article: /blog/news/:slug → reuse the news context
  if (pathname.startsWith('/blog/news/')) return PAGE_CONTEXT['/blog/news'];
  // Dynamic blog post: /blog/:slug
  if (pathname.startsWith('/blog/')) return PAGE_CONTEXT['/blog'];
  return DEFAULT_MESSAGE;
};

const WhatsAppFloatingButton = () => {
  const { pathname } = useLocation();
  const message = useMemo(() => resolveMessage(pathname), [pathname]);

  // Hide on admin pages where it would be intrusive
  if (pathname.startsWith('/admin')) return null;

  const isMembersArea = pathname.startsWith('/members');
  const handleClick = () => {
    const url = `https://wa.me/${WHATSAPP_PHONE_E164}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="צור קשר בוואטסאפ"
      data-testid="whatsapp-floating-btn"
      className={`md:hidden fixed z-[91] rounded-full bg-[#25D366] hover:bg-[#1ebe5d] active:bg-[#179a4f] text-white shadow-lg shadow-[#25D366]/40 hover:shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center ${
        isMembersArea
          ? 'w-10 h-10 left-3 top-[54%] -translate-y-1/2'
          : 'w-14 h-14 bottom-24 left-6'
      }`}
    >
      {/* Outer pulsing ring */}
      <span className="absolute inset-0 rounded-full bg-[#25D366]/30 animate-ping pointer-events-none" aria-hidden="true" />
      <svg viewBox="0 0 24 24" className={`relative ${isMembersArea ? 'w-5 h-5' : 'w-7 h-7'}`} fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
      </svg>
    </button>
  );
};

export default WhatsAppFloatingButton;
